/**
 * Phone detection + redirect to the `/m/` mobile surface.
 *
 * The desktop SPA and the phone surface share one bundle/domain — a visit
 * from a phone should never paint the desktop shell first. `shouldRedirectToMobile`
 * is the pure decision function (unit-tested); `maybeRedirectToMobile` is the
 * impure wrapper that reads the real browser globals and performs the redirect,
 * called as early as possible in `main.tsx`.
 */

export const MOBILE_PATH = '/m/';
export const DESKTOP_OVERRIDE_KEY = 'eddy:prefer-desktop';

/**
 * Paths the phone surface can actually serve. Everything else keeps the
 * desktop app — a deep link (e.g. a shared `/work/abc123` URL) must never be
 * silently rewritten to `/m/`, where it would land on an unrelated chat with
 * no explanation. `/chats` (the chat list) is here because the phone surface
 * has its own chat list one tap away (FRONT-2484); every other page stays on
 * the desktop app and is made to work at phone width instead.
 *
 * The inline theme script in `index.html` hand-copies this list —
 * `theme-boot.test.ts` runs both over a matrix and fails when they drift.
 */
const MOBILE_SERVABLE_PATHS = ['/', '/chat', '/chats'];

function isMobileServablePath(pathname: string): boolean {
  return MOBILE_SERVABLE_PATHS.some(
    (path) => pathname === path || pathname === `${path}/`,
  );
}

/**
 * Pure "is this a phone-shaped viewport" test — a coarse (touch) pointer AND
 * a narrow viewport, narrow meaning the SMALLER of the two dimensions is
 * <=640px, so the check is orientation-independent (an iPhone in landscape is
 * still a phone) while still excluding tablets (an iPad mini's short edge is
 * 744px). Private to this file — `shouldRedirectToMobile` below is its only
 * caller. (Not shared with the theme store's default-preference rule: that
 * composes the whole `shouldRedirectToMobile` decision via
 * `currentRedirectInputs`/`shouldRedirectToMobile` instead of re-deriving the
 * viewport-shape test on its own — see `defaultPreference`'s doc comment in
 * theme-store.ts.)
 */
function isPhoneViewport(input: {
  width: number;
  height: number;
  coarsePointer: boolean;
}): boolean {
  return input.coarsePointer === true && Math.min(input.width, input.height) <= 640;
}

/**
 * Pure, side-effect-free redirect decision. See `isPhoneViewport` for the
 * phone-shape test.
 */
export function shouldRedirectToMobile(input: {
  pathname: string;
  width: number;
  height: number;
  coarsePointer: boolean;
  preferDesktop: boolean;
}): boolean {
  if (input.preferDesktop) return false;
  if (input.pathname === MOBILE_PATH || input.pathname.startsWith(MOBILE_PATH)) {
    return false;
  }
  if (!isMobileServablePath(input.pathname)) return false;
  return isPhoneViewport(input);
}

/**
 * Gathers the real browser signals `shouldRedirectToMobile` needs. Impure
 * (reads `window`/`localStorage`) but returns a plain object so the actual
 * decision stays behind the pure function above. Assumes `window` exists —
 * callers guard that themselves (both current callers already need the guard
 * for their own reasons).
 *
 * Shared by the mount-time redirect check AND the theme store's
 * default-preference rule (FRONT-1342: "the default is 'system' exactly when
 * this browser is on, or would be sent to, the phone surface") so the two can
 * never read the signals — or the desktop opt-out — differently.
 */
export function currentRedirectInputs(): {
  pathname: string;
  width: number;
  height: number;
  coarsePointer: boolean;
  preferDesktop: boolean;
} {
  let preferDesktop = false;
  try {
    preferDesktop = localStorage.getItem(DESKTOP_OVERRIDE_KEY) === '1';
  } catch {
    // Safari private mode (and similar) throws on localStorage access.
    preferDesktop = false;
  }

  return {
    pathname: window.location.pathname,
    width: window.innerWidth,
    height: window.innerHeight,
    coarsePointer: window.matchMedia('(pointer: coarse)').matches,
    preferDesktop,
  };
}

/**
 * Impure wrapper: reads the real browser signals and, if `shouldRedirectToMobile`
 * says so, hard-redirects to the mobile surface (preserving the query string).
 * Safe to call when `window` is undefined (e.g. SSR/test contexts). Returns
 * `true` when a redirect was issued, so the caller can skip mounting the
 * desktop app instead of racing it against the navigation.
 */
export function maybeRedirectToMobile(): boolean {
  if (typeof window === 'undefined') return false;

  const decision = shouldRedirectToMobile(currentRedirectInputs());

  if (decision) {
    window.location.replace(MOBILE_PATH + window.location.search);
  }

  return decision;
}

/** Persist (or clear) the user's explicit choice to stay on the desktop shell. */
export function setPreferDesktop(on: boolean): void {
  try {
    if (on) {
      localStorage.setItem(DESKTOP_OVERRIDE_KEY, '1');
    } else {
      localStorage.removeItem(DESKTOP_OVERRIDE_KEY);
    }
  } catch {
    // Safari private mode (and similar) throws on localStorage access — nothing to do.
  }
}
